import { LoginState, URLParameters } from '../../types';
import queryString from 'query-string';
import { getCastleRequestToken } from '../../castle';
import { getAwsCaptchaTokenValue } from '../../aws-captcha/aws-captcha.helpers';
import { checkFeatureGate } from '../feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';

export type AtlassianAuthenticationLoginParams = {
  username: string;
  password: string;
  state: LoginState;
  query: string;
};

export enum LoginFailureReason {
  AccessDenied = 'access_denied',
  PasswordLeaked = 'password_leaked',
  BlockedUser = 'blocked_user',
  TooManyAttempts = 'too_many_attempts',
  TooManyLogins = 'too_many_logins',
  ClientIpNotAllowed = 'client_ip_not_allowed',
  ClientIpMissing = 'client_ip_missing',
}

export enum LoginError {
  PreconditionFailed = 'precondition_failed',
  UnexpectedError = 'unexpected_error',
}

type LoginSuccessResponse = {
  redirectUri: string;
};

type LoginFailureResponse =
  | { error: 'access_denied' }
  | { error: 'password_leaked' }
  | { error: 'blocked_user' }
  | { error: 'too_many_attempts' }
  | { error: 'too_many_logins' }
  | { error: 'client_ip_not_allowed' }
  | { error: 'client_ip_missing' }
  | { error: 'mfa_required'; token: string };

export type LoginFailure = { kind: 'failure'; reason: LoginFailureReason };
export type LoginSuccess = { kind: 'success'; redirectUri: string };
/**
 * Returned when the WAF challenges the `/rest/authenticate` request with a 405,
 * indicating the user must complete an AWS captcha puzzle before the request can
 * be retried. The caller (loginUsernamePassword) is responsible for showing the
 * puzzle and re-submitting once it is solved.
 */
export type LoginCaptchaRequired = { kind: 'captcha_required' };

function failure(reason: LoginFailureReason): LoginFailure {
  return { kind: 'failure', reason };
}

function success(redirectUri: string): LoginSuccess {
  return { kind: 'success', redirectUri };
}

function captchaRequired(): LoginCaptchaRequired {
  return { kind: 'captcha_required' };
}

export async function login({
  state,
  username,
  password,
  query,
}: AtlassianAuthenticationLoginParams): Promise<
  LoginFailure | LoginSuccess | LoginCaptchaRequired
> {
  const castleToken = await getCastleRequestToken([
    'capability-group--login',
    'capability-name--login-with-username-password',
  ]);
  const token = castleToken || 'dummy-token'; // a token is expected by id-auth in IC and disabled entirely within FedRAMP

  // Whether the AWS Captcha feature is enabled for this boundary.
  const isAwsCaptchaEnabled = checkFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);

  // Whether we want to include the captcha test headers for the `/rest/authenticate` call —
  // used to trigger the WAF challenge E2E in test environments.
  const shouldIncludeTestCaptchaHeaders = checkFeatureGate(
    AidFrontendFeatureGate.ENABLE_HOST_HEADERS_FOR_AWS_CAPTCHA
  );

  const awsCaptchaToken = isAwsCaptchaEnabled ? getAwsCaptchaTokenValue() : '';

  return fetch(`/rest/authenticate${query}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',

      // This header triggers the WAF to issue a captcha challenge (a 405 response).
      ...(shouldIncludeTestCaptchaHeaders
        ? {
            'test-captcha': 'true',
          }
        : {}),

      // The token is only included when the AWS Captcha SDK is loaded and has set the
      // `aws-waf-token` cookie. It will never exist in boundaries without the SDK (e.g. commercial).
      ...(isAwsCaptchaEnabled
        ? {
            'aws-waf-token': awsCaptchaToken,
          }
        : {}),
    },
    body: JSON.stringify({ username, password, state, token }),
  }).then(response => {
    switch (response.status) {
      case 200:
        return response.json().then((r: LoginSuccessResponse) => success(r.redirectUri));
      // Intentional fallthrough, as we want to handle 405 the same way as other unhandled status codes if AWS captcha is disabled.
      // @ts-expect-error Intentional fallthrough
      case 405:
        // 405 is returned only by AWS Captcha when the WAF is asking the user to complete a
        // puzzle. Synthesised from the status code — the response body is empty and never read.
        if (isAwsCaptchaEnabled) {
          return captchaRequired();
        }
      // intentional fallthrough to default case

      case 403:
        return response.json().then((r: LoginFailureResponse) => {
          switch (r.error) {
            case 'access_denied':
              return failure(LoginFailureReason.AccessDenied);
            case 'blocked_user':
              return failure(LoginFailureReason.BlockedUser);
            case 'password_leaked':
              return failure(LoginFailureReason.PasswordLeaked);
            case 'too_many_attempts':
              return failure(LoginFailureReason.TooManyAttempts);
            case 'client_ip_not_allowed':
              return failure(LoginFailureReason.ClientIpNotAllowed);
            case 'client_ip_missing':
              return failure(LoginFailureReason.ClientIpMissing);
            default:
              return Promise.reject(new Error(LoginError.UnexpectedError));
          }
        });
      case 429:
        return response
          .json()
          .then((r: LoginFailureResponse) =>
            r.error === 'too_many_logins'
              ? failure(LoginFailureReason.TooManyLogins)
              : Promise.reject(new Error(LoginError.UnexpectedError))
          );
      case 412:
        return response.json().then((r: LoginFailureResponse) => {
          switch (r.error) {
            case 'mfa_required': {
              const currentQueryParams: URLParameters = queryString.parse(window.location.search);
              currentQueryParams.transactionToken = r.token;
              const currentQueryString = queryString.stringify(currentQueryParams);
              return success(`/login/mfa?${currentQueryString}`);
            }
            default:
              return Promise.reject(new Error(LoginError.PreconditionFailed));
          }
        });
      default:
        return Promise.reject(
          new Error(LoginError.UnexpectedError, {
            cause: {
              description: 'Unexpected response status',
              response,
            },
          })
        );
    }
  });
}
